import React, { useState, useEffect, memo, useRef, useCallback } from "react";
import BillingAddress from "./BillingAddress";
import BillingSummary from "./BillingSummary";
import CardPaymentInfo from "./CardPaymentInfo";
import DigitalPayButton from "./DigitalPayButton";
import ManualPayButton from "./ManualPayButton";
import PayPalButton from "./PaypalButton";
import PersonalInfo from "./PersonalInfo";
import PurchaseInfo from "./PurchaseInfo";

import RaffleApi from "../../../services/resources/RaffleApi";
import { ApiCallErrorMessageHandler } from "../../../services/ApiClientErrorHandler";
import { isMobileView, removeEmptyFromObj } from "../../../lib/utils";
import axios from "axios";
import _cloneDeep from "lodash.clonedeep";
import _set from "lodash.set";
import Big from "big.js";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";

import {
  checkoutSectionElementId,
  purchaseTicketsSectionElementId,
} from "../RaffleByCampus";

const paymentTypes = {
  manual: "manual",
  digital: "digital",
  payPal: "payPal",
};

const PurchaseTickets = (props) => {
  const {
    gregorianYear,
    onCompletePurchase,
    raffleEnrollment,
    raffleTitle,
    referrers,
    systemLists,
    termsAndConditions,
    toggleShowTermsAndConditions,
    team,
  } = props;
  const {
    allowDonorToPayProcessingFee,
    chabadHouseName,
    cociCcProcessingFeePercentage,
    currencyCode,
    defaultTicketOption,
    fullLegalText,
    hasCashPrizeDisclaimer,
    payPalClientID,
    requireDonorAddress,
    requireDonorPhone,
    sellerCountryForStripe,
    sellerEnrollmentID,
    ticketOptions,
    ticketPrice,
  } = raffleEnrollment || {};

  const stripe = useStripe();
  const elements = useElements();

  const [checkoutIsOpen, setCheckoutIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState({
    address: {
      address1: "",
      address2: "",
      country: "",
      city: "",
      state: "",
      zip: "",
    },
    association: "",
    cardHolderFullName: "",
    ccInfoComplete: false,
    didAcceptTerms: false,
    didDonorPayProcessingFee: false,
    email: "",
    firstName: "",
    isPrivate: false,
    isUkGiftAid: false,
    lastName: "",
    paymentNotes: "",
    paymentType: "CreditCard",
    phone: "",
    referrerID: "",
    source: "Website",
    ticketQuantity: defaultTicketOption || 5,
    ticketOptions: ticketOptions || [3, 5, 10, 15],
    tribute: "",
  });
  const [orderIncompleteFields, setOrderIncompleteFields] = useState([]);
  const [orderIsIncomplete, setOrderIsIncomplete] = useState(true);
  const [orderValidationErrors, setOrderValidationErrors] = useState([]);
  const [paymentType, setPaymentType] = useState(paymentTypes.manual);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const checkIncompleteOrder = useCallback(
    (_paymentType = paymentType) => {
      const {
        address: { address1, city, country, state, zip },
        cardHolderFullName,
        ccInfoComplete,
        didAcceptTerms,
        email,
        firstName,
        lastName,
        phone,
        ticketQuantity,
      } = order;

      //incomplete fields, ordered as they appear in the UI
      const orderIncompleteFields = [
        !ticketQuantity ? "Ticket Quantity" : "",
        !firstName ? "First Name" : "",
        !lastName ? "Last Name" : "",
        !email ? "Email" : "",
        !phone && requireDonorPhone ? "Phone" : "",
        !didAcceptTerms ? "Terms and Conditions agreement" : "",
      ].filter((f) => f);

      if (_paymentType === paymentTypes.manual) {
        if (requireDonorAddress) {
          if (!address1) {
            orderIncompleteFields.push("Street Address");
          }
          if (!city) {
            orderIncompleteFields.push("City");
          }
          if (!country) {
            orderIncompleteFields.push("Country");
          }
          if (!state) {
            orderIncompleteFields.push("State");
          }
          if (!zip) {
            orderIncompleteFields.push("Zip");
          }
        }

        if (!ccInfoComplete) {
          orderIncompleteFields.push("Credit Card Info");
        } else if (!cardHolderFullName) {
          orderIncompleteFields.push("Cardholder Name");
        }
      }

      if (orderValidationErrors.length) {
        orderIncompleteFields.push(`Valid ${orderValidationErrors.join(" ,")}`);
      }

      var orderIsIncomplete = !!orderIncompleteFields.length;
      return {
        orderIncompleteFields,
        orderIsIncomplete,
      };
    },
    [
      order,
      orderValidationErrors,
      requireDonorAddress,
      requireDonorPhone,
      paymentType,
    ]
  );

  let ticketPriceAmount = 0,
    processingFeeDonorAmount = 0,
    orderTotal = 0;
  if (ticketPrice && order.ticketQuantity) {
    ticketPriceAmount = Big(ticketPrice).times(order.ticketQuantity);
    if (order.didDonorPayProcessingFee) {
      processingFeeDonorAmount = ticketPriceAmount.times(
        cociCcProcessingFeePercentage / 100
      );
    }
    orderTotal = ticketPriceAmount.plus(processingFeeDonorAmount);
  }

  const apiSignalRef = useRef(axios.CancelToken.source());
  useEffect(() => {
    const _apiSignal = apiSignalRef.current;
    return () => {
      _apiSignal.cancel();
    };
  }, []);

  useEffect(() => {
    //preselect referrer/team in dropdown
    if (team) {
      setOrder((o) => ({ ...o, referrerID: team.id }));
    }
  }, [team]);

  useEffect(() => {
    if (orderIsIncomplete) {
      const { orderIncompleteFields, orderIsIncomplete } =
        checkIncompleteOrder();
      setOrderIncompleteFields(orderIncompleteFields);
      setOrderIsIncomplete(orderIsIncomplete);
    }
  }, [orderIsIncomplete, checkIncompleteOrder]);

  const onChangeOrder = useCallback(
    (name, value, otherUpdates) => {
      let _order = _cloneDeep(order);
      _set(_order, name, value);

      //if first/last name is updated, update cardHolderFullName to match
      const { cardHolderFullName, firstName, lastName } = order;
      if (
        (name === "firstName" || name === "lastName") &&
        (!cardHolderFullName ||
          cardHolderFullName === `${firstName} ${lastName}`)
      ) {
        //update only if not manually overridden
        otherUpdates = {
          ...(otherUpdates || {}),
          cardHolderFullName: `${_order.firstName} ${_order.lastName}`,
        };
      }

      if (otherUpdates) {
        Object.keys(otherUpdates).forEach((update) =>
          _set(_order, update, otherUpdates[update])
        );
      }
      setOrder(_order);
      if (orderIsIncomplete) {
        const { orderIncompleteFields, orderIsIncomplete } =
          checkIncompleteOrder();
        setOrderIncompleteFields(orderIncompleteFields);
        setOrderIsIncomplete(orderIsIncomplete);
      }
    },
    [order, orderIsIncomplete, checkIncompleteOrder]
  );

  const onChangeEventOrder = useCallback(
    (event) => {
      onChangeOrder(
        event.target.name,
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value
      );
    },
    [onChangeOrder]
  );

  const onCheckoutButtonClick = useCallback(
    (paymentType) => {
      const { orderIncompleteFields, orderIsIncomplete } =
        checkIncompleteOrder(paymentType);
      setErrorMessage("");
      setOrderIncompleteFields(orderIncompleteFields);
      setOrderIsIncomplete(orderIsIncomplete);
      setPaymentType(paymentType);
      setSubmitAttempted(true);

      return !orderIsIncomplete;
    },
    [checkIncompleteOrder]
  );

  const onDigitalCheckoutButtonClick = useCallback(() => {
    return onCheckoutButtonClick(paymentTypes.digital);
  }, [onCheckoutButtonClick]);

  const onPayPalButtonClick = useCallback(() => {
    return onCheckoutButtonClick(paymentTypes.payPal);
  }, [onCheckoutButtonClick]);

  const onSubmitPayment = useCallback(
    async (paymentType = paymentTypes.manual, paymentData = {}) => {
      const {
        completeDigitalPayment = null,
        digitalPayPaymentMethod = null,
        payPalOrderId,
      } = paymentData;

      const { orderIncompleteFields, orderIsIncomplete } =
        checkIncompleteOrder(paymentType);
      setErrorMessage("");
      setOrderIncompleteFields(orderIncompleteFields);
      setOrderIsIncomplete(orderIsIncomplete);
      setPaymentType(paymentType);
      setSubmitAttempted(true);

      if (orderIsIncomplete) {
        if (paymentType === paymentTypes.digital) {
          completeDigitalPayment("fail");
        }
        return;
      }
      const { address, email } = order;
      const { ccInfoComplete, cardHolderFullName, ...otherOrderDetails } =
        order; //destructuring separately so address and email will stay in otherOrderDetails

      const orderForSubmission = _cloneDeep(otherOrderDetails);
      orderForSubmission.sellerEnrollmentId = sellerEnrollmentID;

      setLoading(true);

      let paymentMethodId;

      if (paymentType === paymentTypes.payPal) {
        orderForSubmission.paymentType = "PayPal";
        orderForSubmission.payPalOrderID = payPalOrderId;
      } else {
        orderForSubmission.paymentType = "CreditCard";

        if (paymentType === paymentTypes.digital) {
          paymentMethodId = digitalPayPaymentMethod.id;
        } else {
          const billingDetails = {
            name: cardHolderFullName,
          };
          const hasAddress =
            address.address1 ||
            address.address2 ||
            address.city ||
            address.state ||
            address.country ||
            address.zip;
          if (hasAddress) {
            billingDetails.address = {
              line1: address.address1,
              line2: address.address2,
              city: address.city,
              state: address.state,
              postal_code: address.zip,
              country: (
                systemLists.countries.find(
                  (country) => country.name === address.country
                ) || {}
              ).code,
            };
          }
          const cardElement = elements?.getElement(CardNumberElement);
          let { error: paymentMethodError, paymentMethod } =
            await stripe?.createPaymentMethod({
              type: "card",
              card: cardElement,
              billing_details: billingDetails,
            });

          if (paymentMethodError) {
            setErrorMessage(
              paymentMethodError.message ||
                "Sorry, we are unable to process your credit card payment.  Please try again."
            );
            setLoading(false);
            return;
          }
          paymentMethodId = paymentMethod.id;
        }

        //Additional check in case paymentMethodId was not created and no error was thrown
        if (paymentMethodId) {
          orderForSubmission.paymentMethodId = paymentMethodId;
        } else {
          setErrorMessage(
            "Sorry, we are unable to process your credit card payment.  Please try again."
          );
          setLoading(false);
          if (paymentType === paymentTypes.digital) {
            completeDigitalPayment("fail");
          }
          return;
        }
      }

      removeEmptyFromObj(orderForSubmission);
      if (orderForSubmission.email) {
        orderForSubmission.email = orderForSubmission.email.trim();
      }

      try {
        let orderResponse = await RaffleApi.createOrder(
          apiSignalRef.current.token,
          orderForSubmission
        );

        if (paymentType === paymentTypes.digital) {
          //hide digital pay window to allow SCA Action window interaction
          completeDigitalPayment("success");
        }

        if (orderResponse.requiresAction) {
          //SCA Action Required
          const { error: paymentIntentError, paymentIntent } =
            await stripe?.handleCardAction(
              orderResponse.paymentIntentClientSecret
            );

          if (paymentIntentError) {
            setErrorMessage(
              paymentIntentError.message ||
                "Sorry, we are unable to process your credit card payment.  Please try again."
            );
            setLoading(false);
            return;
          } else {
            // The card action has been handled. The PaymentIntent can be confirmed again on the server
            delete orderForSubmission.paymentMethodId;
            orderForSubmission.paymentIntentId = paymentIntent.id;

            orderResponse = await RaffleApi.createOrder(
              apiSignalRef.current.token,
              orderForSubmission
            );
            // Checking again if still requiresAction for some reason and if so returning error message
            if (orderResponse.requiresAction) {
              setErrorMessage(
                "Sorry, we are unable to process your credit card payment.  Please try again."
              );
              setLoading(false);
              return;
            }
          }
        }

        onCompletePurchase(email);
      } catch (err) {
        if (!axios.isCancel(err)) {
          setErrorMessage(ApiCallErrorMessageHandler(err));
          setLoading(false);

          if (paymentType === paymentTypes.digital) {
            completeDigitalPayment("fail");
          }
        }
      }
    },
    [
      order,
      stripe,
      systemLists.countries,
      onCompletePurchase,
      sellerEnrollmentID,
      checkIncompleteOrder,
      elements,
    ]
  );

  const onSubmitManualPay = useCallback(
    (event) => {
      const submitManualPay = async (event) => {
        if (event) {
          event.preventDefault();
        }
        return await onSubmitPayment();
      };
      submitManualPay(event);
    },
    [onSubmitPayment]
  );

  const onSubmitDigitalPay = useCallback(
    (paymentMethod, completeDigitalPayment) => {
      const submitDigitalPay = async (
        paymentMethod,
        completeDigitalPayment
      ) => {
        return await onSubmitPayment(paymentTypes.digital, {
          digitalPayPaymentMethod: paymentMethod,
          completeDigitalPayment,
        });
      };
      submitDigitalPay(paymentMethod, completeDigitalPayment);
    },
    [onSubmitPayment]
  );

  const onSubmitPayPalPay = useCallback(
    (payPalOrderId) => {
      const submitPayPalPay = async (payPalOrderId) => {
        return await onSubmitPayment(paymentTypes.payPal, {
          payPalOrderId,
        });
      };
      submitPayPalPay(payPalOrderId);
    },
    [onSubmitPayment]
  );

  const updateOrderValidation = useCallback(
    (name, isValid) => {
      setOrderValidationErrors(
        isValid
          ? orderValidationErrors.filter((err) => err !== name)
          : [...orderValidationErrors, name]
      );

      if (orderIsIncomplete && isValid) {
        const { orderIncompleteFields, orderIsIncomplete } =
          checkIncompleteOrder();
        setOrderIncompleteFields(orderIncompleteFields);
        setOrderIsIncomplete(orderIsIncomplete);
      }
    },
    [checkIncompleteOrder, orderIsIncomplete, orderValidationErrors]
  );

  const onChangeQuantity = useCallback(
    (qty) => {
      onChangeOrder("ticketQuantity", qty);
    },
    [onChangeOrder]
  );

  const toggleDidAcceptTerms = useCallback(
    (didAcceptTerms) => {
      onChangeOrder("didAcceptTerms", didAcceptTerms);
    },
    [onChangeOrder]
  );

  const clearErrorMessage = useCallback(() => {
    setErrorMessage("");
  }, []);

  return (
    <>
      <form
        action="/autocomplete"
        autoComplete="on"
        className="checkout-form"
        id={purchaseTicketsSectionElementId}
        method="post"
        onSubmit={onSubmitManualPay}
      >
        <PurchaseInfo
          chabadHouseName={chabadHouseName}
          currencyCode={currencyCode}
          onChangeQuantity={onChangeQuantity}
          submitAttempted={submitAttempted}
          ticketOptions={order.ticketOptions}
          ticketQuantity={order.ticketQuantity}
          ticketPrice={ticketPrice}
          termsAndConditions={termsAndConditions}
          toggleShowTermsAndConditions={toggleShowTermsAndConditions}
        />
        <BillingSummary
          allowDonorToPayProcessingFee={allowDonorToPayProcessingFee}
          cociCcProcessingFeePercentage={cociCcProcessingFeePercentage}
          currencyCode={currencyCode}
          didDonorPayProcessingFee={order.didDonorPayProcessingFee}
          onChangeEventOrder={onChangeEventOrder}
          orderTotal={orderTotal}
          processingFeeDonorAmount={processingFeeDonorAmount}
          setCheckoutIsOpen={setCheckoutIsOpen}
          submitAttempted={submitAttempted}
          ticketPriceAmount={ticketPriceAmount}
          ticketQuantity={order.ticketQuantity}
        />
        {!checkoutIsOpen ? (
          <div className="flex flex-justify-center">
            <button
              className="btn btn-rounded mb-40 pointer"
              onClick={() => {
                setCheckoutIsOpen(true);
                if (isMobileView()) window.scrollBy(0, 1); // scroll 1px just to trigger the scroll event listener to hide the sticky purchase button on mobile
              }}
            >
              Checkout
            </button>
          </div>
        ) : (
          <div id={checkoutSectionElementId}>
            <PersonalInfo
              associations={systemLists.associations}
              countries={systemLists.countries}
              currencyCode={currencyCode}
              gregorianYear={gregorianYear}
              legalText={fullLegalText}
              onChange={onChangeOrder}
              onChangeEvent={onChangeEventOrder}
              order={order}
              orderValidationErrors={orderValidationErrors}
              referrers={referrers}
              requireDonorPhone={requireDonorPhone}
              submitAttempted={submitAttempted}
              termsAndConditions={termsAndConditions}
              toggleDidAcceptTerms={toggleDidAcceptTerms}
              toggleShowTermsAndConditions={toggleShowTermsAndConditions}
              updateOrderValidation={updateOrderValidation}
              team={team}
            />
            <div
              style={{
                borderTop: "2px solid rgba(0, 0, 0, .1)",
                width: 1200,
                maxWidth: "100%",
                margin: "48px auto 0",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <p className="fs-20 fw-600 mt-24 mb-24 text-center">
                Select a Payment Method
              </p>
              <div className="payment-methods-grid">
                <DigitalPayButton
                  checkIsOrderCompleteOnClick={onDigitalCheckoutButtonClick}
                  country={sellerCountryForStripe}
                  createPaymentRequest={stripe?.paymentRequest}
                  currencyCode={currencyCode}
                  orderTotal={orderTotal}
                  loading={paymentType === paymentTypes.digital && loading}
                  setErrorMessage={setErrorMessage}
                  submitPayment={onSubmitDigitalPay}
                />
                <PayPalButton
                  chabadHouseName={props.chabadHouseName}
                  checkIsOrderCompleteOnClick={onPayPalButtonClick}
                  clientId={payPalClientID}
                  country={sellerCountryForStripe}
                  currencyCode={currencyCode}
                  itemPrice={ticketPrice}
                  itemQuantity={order.ticketQuantity}
                  ticketPriceAmount={ticketPriceAmount}
                  processingFeeDonorAmount={processingFeeDonorAmount}
                  orderTotal={orderTotal}
                  loading={paymentType === paymentTypes.payPal && loading}
                  setErrorMessage={setErrorMessage}
                  purchaseType={raffleTitle}
                  submitPayment={onSubmitPayPalPay}
                />
              </div>
              {(paymentType === paymentTypes.digital ||
                paymentType === paymentTypes.payPal) &&
                (submitAttempted && orderIsIncomplete ? (
                  <p
                    className="error-text text-center mt-16"
                    style={{ padding: "0 16px" }}
                  >
                    Please complete required fields:{" "}
                    {orderIncompleteFields && orderIncompleteFields.join(", ")}
                  </p>
                ) : (
                  errorMessage && (
                    <div className="error-box flex-align-center">
                      <i className="material-icons mr-8 large-text">warning</i>{" "}
                      {errorMessage}
                    </div>
                  )
                ))}
              <p className="fs-20 mobile-fs-16 mt-24 mb-40">
                or enter your credit card information below
              </p>
            </div>
            <div className="billing-info">
              <CardPaymentInfo
                cardHolderFullName={order.cardHolderFullName}
                ccInfoComplete={order.ccInfoComplete}
                isManualPay={paymentType === paymentTypes.manual}
                onChange={onChangeOrder}
                onChangeEvent={onChangeEventOrder}
                submitAttempted={submitAttempted}
                CardNumberElement={CardNumberElement}
                CardCvcElement={CardCvcElement}
                CardExpiryElement={CardExpiryElement}
              />
              <BillingAddress
                address={order.address}
                countries={systemLists.countries}
                hasCashPrizeDisclaimer={hasCashPrizeDisclaimer}
                isManualPay={paymentType === paymentTypes.manual}
                onChange={onChangeOrder}
                onChangeEvent={onChangeEventOrder}
                submitAttempted={submitAttempted}
                requireDonorAddress={requireDonorAddress}
              />
            </div>
            <div>
              <ManualPayButton
                clearErrorMessage={clearErrorMessage}
                currencyCode={currencyCode}
                orderIncompleteFields={orderIncompleteFields}
                orderIsIncomplete={orderIsIncomplete}
                orderTotal={orderTotal}
                {...(paymentType === paymentTypes.manual
                  ? {
                      errorMessage,
                      loading,
                      submitAttempted,
                    }
                  : {})}
              />
            </div>
          </div>
        )}
      </form>
    </>
  );
};

export default memo(PurchaseTickets);
